/**
 * A manutenção da Tabela Fipe: a barra, o dicionário de campos e a normalização.
 *
 * **A barra é um acervo finito e contável**, não um fluxo: a Fipe às vezes
 * descreve duas variantes num registro só — `"Legend 3.2/3.5"` — e alguém
 * precisa separá-las. Em produção são **586 pendentes** de 618 com barra. Dá
 * para terminar.
 *
 * **E é um `/candidato` antes de gravar.** A API propõe as variantes, quem
 * revisa corrige o texto, e só então confirma. Nada é gravado na proposta.
 *
 * **A normalização mexe na tabela inteira** — recalcula cilindrada, válvulas,
 * turbo, potência e nome curto de cada linha a partir do texto do veículo. São
 * ~6.900 registros. É a operação que merece confirmação, não um botão solto.
 *
 * Tudo aqui é **dado global**: a trava recusa gravação em desenvolvimento.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

export type RegistroFipe = {
  id: number
  marca: string
  veiculo: string
  nomeCurto: string
  fipeCodigo: string
  chave: string
  anoIni: number
  anoFim: number
  combustivel: string
  barra: boolean
  conferido: boolean
}

export type VarianteDaBarra = { tabelaFipeId: number; veiculo: string }

export type CandidatoDaBarra = {
  pai: RegistroFipe
  qtdBarra: number
  /** A API diz se dá para seguir; `false` costuma ser texto que não se divide. */
  continuar: boolean
  variantes: VarianteDaBarra[]
}

export function usarRegistrosComBarra(todos: boolean) {
  return useQuery({
    queryKey: ["fipe-barra", todos] as const,
    staleTime: 60_000,
    queryFn: () => obter<RegistroFipe[]>("api/tabela-fipe/barra", { todos }),
  })
}

/** A proposta de divisão. **Não grava nada** — é a conferência da operação. */
export function usarCandidatoDaBarra(id: number) {
  return useQuery({
    queryKey: ["fipe-barra-candidato", id] as const,
    enabled: id > 0,
    staleTime: Infinity,
    queryFn: () => obter<CandidatoDaBarra>(`api/tabela-fipe/barra/${id}/candidato`, {}),
  })
}

/**
 * Confirma a divisão.
 *
 * `idBarra` é o **pai** — o registro que tinha a barra. É o que amarra as
 * variantes à origem; sem ele a divisão viraria registro solto.
 */
export function usarConfirmarBarra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { idBarra, variantes }: { idBarra: number; variantes: VarianteDaBarra[] },
    ) => {
      await api.post("api/tabela-fipe/barra/confirmar", {
        variantes: variantes.map((v) => ({ ...v, idBarra })),
      }, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fipe-barra"] }),
  })
}

export type ResumoDaNormalizacao = {
  processadas: number
  atualizadas: number
  erros: number
}

/** Recalcula os campos derivados da tabela inteira — ~6.900 registros. */
export function usarNormalizarFipe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<ResumoDaNormalizacao>("api/tabela-fipe/normalizar", null, foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fipe-barra"] }),
  })
}

// ------------------------------------------------------------------ campos

/**
 * O dicionário que a normalização usa: para cada campo derivado, que texto do
 * veículo vira que valor. Em produção são 123 linhas em 7 campos — cilindrada é
 * quase metade delas.
 */
export type CampoDaFipe = { id: number; campo: string; texto: string; retorno: string }

export function usarCamposDaFipe() {
  return useQuery({
    queryKey: ["fipe-campos"] as const,
    staleTime: 60_000,
    queryFn: () => obter<CampoDaFipe[]>("api/tabela-fipe-campos", {}),
  })
}

export function usarSalvarCampoDaFipe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id?: number; dados: Omit<CampoDaFipe, "id"> }) => {
      if (id) await api.put(`api/tabela-fipe-campos/${id}`, dados, foraDoProjeto())
      else await api.post("api/tabela-fipe-campos", dados, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fipe-campos"] }),
  })
}

export function usarExcluirCampoDaFipe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/tabela-fipe-campos/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fipe-campos"] }),
  })
}

// ------------------------------------------------------------------ veículo/ano

/**
 * O atalho "Veículo/Ano": marca, nome curto e ano, sem passar pelos filtros.
 *
 * Devolve a **página** da pesquisa (`{ itens }`), não um array solto — é a mesma
 * forma da listagem da Fipe.
 */
export function usarFipePorVeiculoAno(p: { marca: string; nomeCurto: string; ano: number }) {
  const pronto = p.marca.trim() !== "" || p.nomeCurto.trim() !== "" || p.ano > 0
  return useQuery({
    queryKey: ["fipe-veiculo-ano", p] as const,
    enabled: pronto,
    queryFn: () => obter<{ itens: RegistroFipe[]; total?: number }>("api/tabela-fipe/veiculo-ano", {
      marca: p.marca || undefined,
      nomeCurto: p.nomeCurto || undefined,
      ano: p.ano > 0 ? p.ano : undefined,
    }),
  })
}
